import type { BrandRequestListItem } from "@nlc/shared-types";

import type { TableConfig } from "@/features/requests/types/tables";

import {
  assigneeCell,
  dateCell,
  dueDateCellColored,
  nameCell,
  requestDesignTypeCell,
  statusCell,
} from "@/features/requests/config/helpers/commonCells";
import { renderRating } from "@/features/requests/config/helpers/renderHelpers";

import type { BrandRequestFilterId } from "./filterConfig";

const approvedTableConfig: TableConfig<BrandRequestListItem> = {
  columns: [
    {
      accessor: "name",
      cellRenderer: nameCell,
      header: "Name",
      key: "name",
      sortable: true,
      width: 280,
    },
    {
      accessor: "assignee",
      cellRenderer: assigneeCell,
      header: "Assignee",
      key: "assignee",
      sortable: true,
      width: 160,
    },
    {
      accessor: "rating",
      cellRenderer: (value: unknown) =>
        renderRating(value as null | number | undefined),
      header: "Rating",
      key: "rating",
      sortable: true,
      width: 100,
    },
    {
      accessor: "completedDate",
      cellRenderer: dateCell,
      header: "Completed date",
      key: "completedDate",
      sortable: true,
      width: 150,
    },
    {
      accessor: "requestType",
      cellRenderer: requestDesignTypeCell,
      header: "Request type",
      key: "requestType",
      sortable: true,
      width: 180,
    },
  ],
  defaultSort: {
    column: "completedDate",
    direction: "desc",
  },
  enableSorting: true,
  id: "brand-approved-requests",
};

const allTableConfig: TableConfig<BrandRequestListItem> = {
  columns: [
    {
      accessor: "name",
      cellRenderer: nameCell,
      header: "Name",
      key: "name",
      sortable: true,
      width: 240,
    },
    {
      accessor: "assignee",
      cellRenderer: assigneeCell,
      header: "Assignee",
      key: "assignee",
      sortable: true,
      width: 140,
    },
    {
      accessor: "status",
      cellRenderer: statusCell,
      header: "Status",
      key: "status",
      sortable: true,
      width: 180,
    },
    {
      accessor: "rating",
      cellRenderer: (value: unknown) =>
        renderRating(value as null | number | undefined),
      header: "Rating",
      key: "rating",
      sortable: true,
      width: 100,
    },
    {
      accessor: "createdAt",
      cellRenderer: dateCell,
      header: "Created",
      key: "createdAt",
      sortable: true,
      width: 130,
    },
    {
      accessor: "completedDate",
      cellRenderer: dateCell,
      header: "Completed date",
      key: "completedDate",
      sortable: true,
      width: 140,
    },
    {
      accessor: "requestType",
      cellRenderer: requestDesignTypeCell,
      header: "Request type",
      key: "requestType",
      sortable: true,
      width: 170,
    },
  ],
  defaultSort: {
    column: "createdAt",
    direction: "desc",
  },
  enableSorting: true,
  id: "brand-all-requests",
};

const dueTodayTableConfig: TableConfig<BrandRequestListItem> = {
  columns: [
    {
      accessor: "name",
      cellRenderer: nameCell,
      header: "Name",
      key: "name",
      sortable: true,
      width: 280,
    },
    {
      accessor: "assignee",
      cellRenderer: assigneeCell,
      header: "Assignee",
      key: "assignee",
      sortable: true,
      width: 160,
    },
    {
      accessor: "status",
      cellRenderer: statusCell,
      header: "Status",
      key: "status",
      sortable: true,
      width: 160,
    },
    {
      accessor: "createdAt",
      cellRenderer: dateCell,
      header: "Created",
      key: "createdAt",
      sortable: true,
      width: 140,
    },
    {
      accessor: "requestType",
      cellRenderer: requestDesignTypeCell,
      header: "Request type",
      key: "requestType",
      sortable: true,
      width: 180,
    },
  ],
  defaultSort: {
    column: "createdAt",
    direction: "desc",
  },
  enableSorting: true,
  id: "brand-due-today-requests",
};

const activeTableConfig: TableConfig<BrandRequestListItem> = {
  columns: [
    {
      accessor: "name",
      cellRenderer: nameCell,
      header: "Name",
      key: "name",
      sortable: true,
      width: 250,
    },
    {
      accessor: "assignee",
      cellRenderer: assigneeCell,
      header: "Assignee",
      key: "assignee",
      sortable: true,
      width: 150,
    },
    {
      accessor: "dueDate",
      cellRenderer: dueDateCellColored,
      header: "Due",
      key: "dueDate",
      sortable: true,
      width: 130,
    },
    {
      accessor: "status",
      cellRenderer: statusCell,
      header: "Status",
      key: "status",
      sortable: true,
      width: 150,
    },
    {
      accessor: "createdAt",
      cellRenderer: dateCell,
      header: "Created date",
      key: "createdAt",
      sortable: true,
      width: 140,
    },
    {
      accessor: "requestType",
      cellRenderer: requestDesignTypeCell,
      header: "Request type",
      key: "requestType",
      sortable: true,
      width: 180,
    },
  ],
  defaultSort: {
    column: "dueDate",
    direction: "asc",
  },
  enableSorting: true,
  id: "brand-active-requests",
};

const queuedTableConfig: TableConfig<BrandRequestListItem> = {
  columns: [
    {
      accessor: "name",
      cellRenderer: nameCell,
      header: "Name",
      key: "name",
      sortable: true,
      width: 400,
    },
    {
      accessor: "createdAt",
      cellRenderer: dateCell,
      header: "Created",
      key: "createdAt",
      sortable: true,
      width: 180,
    },
    {
      accessor: "requestType",
      cellRenderer: requestDesignTypeCell,
      header: "Request type",
      key: "requestType",
      sortable: true,
      width: 200,
    },
  ],
  defaultSort: {
    column: "order",
    direction: "asc",
  },
  enableSorting: true,
  id: "brand-queued-requests",
};

const TABLE_CONFIG_MAP: Record<
  BrandRequestFilterId,
  TableConfig<BrandRequestListItem>
> = {
  active: activeTableConfig,
  all: allTableConfig,
  approved: approvedTableConfig,
  "due-today": dueTodayTableConfig,
  queued: queuedTableConfig,
};

export function getTableConfigForFilter(
  filterId: BrandRequestFilterId,
): TableConfig<BrandRequestListItem> {
  return TABLE_CONFIG_MAP[filterId];
}
